import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Share2,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  Navigation,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MapPin,
  Store,
  Clock,
  CreditCard,
  ShoppingBag,
  TestTube,
  Repeat,
  ChevronRight as ChevronRightIcon,
  MessageCircle,
} from 'lucide-react';
import shopService, { Shop, WeekSchedule, OpeningHour } from '../services/shopService';
import StartChatButton from '../components/StartChatButton';
import FavoriteButton from '../components/profile/FavoriteButton';

const TEAL = '#A855F7';
const WARN = '#F59E0B';

function pad2(n?: number): string {
  return String(typeof n === 'number' ? n : 0).padStart(2, '0');
}

function formatOpeningHour(oh?: OpeningHour | null): string {
  if (!oh || !oh.start || !oh.end) return '';
  return `${pad2(oh.start.hour)}:${pad2(oh.start.minute)} - ${pad2(oh.end.hour)}:${pad2(oh.end.minute)}`;
}

const DAY_NAMES_DE: { key: keyof WeekSchedule; label: string }[] = [
  { key: 'monday', label: 'Montag' },
  { key: 'tuesday', label: 'Dienstag' },
  { key: 'wednesday', label: 'Mittwoch' },
  { key: 'thursday', label: 'Donnerstag' },
  { key: 'friday', label: 'Freitag' },
  { key: 'saturday', label: 'Samstag' },
  { key: 'sunday', label: 'Sonntag' },
];

function getCurrentDayKey(): keyof WeekSchedule {
  const idx = (new Date().getDay() + 6) % 7;
  return DAY_NAMES_DE[idx].key;
}

interface ScheduleRow {
  dayKey: keyof WeekSchedule;
  dayLabel: string;
  time: string;
  isToday: boolean;
}

function buildScheduleRows(schedule?: WeekSchedule | null): ScheduleRow[] {
  if (!schedule) return [];
  const today = getCurrentDayKey();
  const rows: ScheduleRow[] = [];
  for (const day of DAY_NAMES_DE) {
    const hours = (schedule[day.key] as OpeningHour[] | undefined) || [];
    for (const oh of hours) {
      const time = formatOpeningHour(oh);
      if (time) rows.push({ dayKey: day.key, dayLabel: day.label, time, isToday: day.key === today });
    }
  }
  return rows;
}

const ShopDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState<Shop | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDescriptionExpanded, setDescExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [externalLink, setExternalLink] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res = await shopService.getShopBySlug(slug);
      const s: Shop | null = res?.data || null;
      if (!s || typeof s !== 'object') {
        setNotFound(true);
        setShop(null);
        return;
      }
      if (s.visible === false) {
        setNotFound(true);
        setShop(null);
        return;
      }
      setShop(s);
    } catch (err) {
      console.error('Fehler beim Laden des Shops:', err);
      setNotFound(true);
      setShop(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (copyToast) {
      const tid = setTimeout(() => setCopyToast(null), 2000);
      return () => clearTimeout(tid);
    }
  }, [copyToast]);

  const allImages = useMemo<string[]>(() => {
    if (!shop) return [];
    const list: string[] = [];
    if (shop.image) list.push(shop.image);
    if (Array.isArray(shop.images)) {
      for (const img of shop.images) {
        if (img && !list.includes(img)) list.push(img);
      }
    }
    return list;
  }, [shop]);

  const lightboxList = useMemo(
    () => allImages.map((id) => shopService.buildFileUrl(id) || ''),
    [allImages]
  );

  const openingRows = useMemo(() => buildScheduleRows(shop?.openingHours), [shop]);

  const paymentMethods = useMemo<string[]>(() => {
    if (!shop || !Array.isArray(shop.paymentMethods)) return [];
    return shop.paymentMethods.filter((m): m is string => typeof m === 'string' && m.length > 0);
  }, [shop]);

  const hasContact = !!(shop?.phone || shop?.email || shop?.shopLink);
  const description = shop?.description || '';
  const isDescriptionLong = description.length > 250;
  const displayedDescription =
    isDescriptionExpanded || !isDescriptionLong
      ? description
      : `${description.substring(0, 250)}...`;

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = shop?.name ? `${shop.name} | DartCloud` : 'DartCloud Shop';
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      }
    } catch (_e) { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyToast('Link in Zwischenablage kopiert');
    } catch (_e) {
      setCopyToast('Teilen nicht möglich');
    }
  };

  const handleCopy = async (text: string, toastMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(toastMsg);
    } catch (_e) {
      setCopyToast('Kopieren fehlgeschlagen');
    }
  };

  const handleOpenMaps = () => {
    if (!shop?.address) return;
    const { latitude, longitude, label } = shop.address;
    let url: string;
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    } else if (label) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
    } else {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCallPhone = (phone?: string | null) => {
    if (!phone) return;
    window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
  };

  const handleSendEmail = (email?: string | null) => {
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  const handleOpenShopLink = (url?: string | null) => {
    if (!url) return;
    setExternalLink(url);
  };

  const previousImage = () => {
    if (allImages.length <= 1) return;
    setCurrentImageIndex((idx) => (idx > 0 ? idx - 1 : allImages.length - 1));
  };
  const nextImage = () => {
    if (allImages.length <= 1) return;
    setCurrentImageIndex((idx) => (idx < allImages.length - 1 ? idx + 1 : 0));
  };
  const openImageLightbox = (idx: number) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-zinc-700 border-t-white rounded-full animate-spin" />
          <div className="text-sm text-zinc-400">Shop laden...</div>
        </div>
      </div>
    );
  }

  if (notFound || !shop) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-zinc-500" />
          <h1 className="text-2xl font-bold mb-2">Shop nicht gefunden</h1>
          <p className="text-zinc-400 mb-6">Der gesuchte Shop existiert nicht oder ist nicht öffentlich.</p>
          <button
            onClick={() => navigate('/shops')}
            className="px-5 py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-zinc-200 transition"
          >
            Zur Shop-Übersicht
          </button>
        </div>
      </div>
    );
  }

  const metaDescription =
    description.length > 0
      ? description.length > 160 ? `${description.substring(0, 157)}...` : description
      : `Dart-Shop ${shop.name || ''} auf DartCloud${shop.address?.label ? ` - Dart-Equipment in ${shop.address.label}` : ''}`;
  const heroImageUrl = shopService.buildFileUrl(allImages[0]);
  const currentImageUrl = shopService.buildFileUrl(allImages[currentImageIndex]);
  const logoUrl = shopService.buildFileUrl(shop.logo);
  const addressLabel = shopService.formatAddress(shop.address);
  const hasCoords = typeof shop.address?.latitude === 'number' && typeof shop.address?.longitude === 'number';

  return (
    <>
      <Helmet>
        <title>{shop.name ? `${shop.name} | DartCloud` : 'Shop | DartCloud'}</title>
        <meta name="description" content={metaDescription} />
        {heroImageUrl && <meta property="og:image" content={heroImageUrl} />}
        <meta property="og:title" content={shop.name ? `${shop.name} | DartCloud` : 'Shop | DartCloud'} />
        <meta property="og:description" content={metaDescription} />
        <link rel="canonical" href={`https://dartcloudapp.de/shop/${slug}`} />
      </Helmet>

      {/* HERO HEADER */}
      <section
        className="w-full border-b border-zinc-800"
        style={{ background: 'linear-gradient(135deg, #1a0f1f 0%, #2a1838 100%)' }}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 py-5 sm:py-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 transition text-white"
              aria-label="Zurück"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <FavoriteButton
                id={shop.id || shop._id || ''}
                type="SHOP"
                className="p-3 rounded-xl border border-zinc-700 bg-zinc-800 hover:bg-zinc-700"
              />
              <ActionButton icon={<Share2 size={20} />} onClick={handleShare} />
            </div>
          </div>

          <div className="mt-4 flex items-start gap-4">
            {logoUrl && (
              <div className="shrink-0 rounded-2xl bg-white overflow-hidden shadow-lg" style={{ width: 70, height: 70 }}>
                <img src={logoUrl} alt={shop.name || 'Logo'} className="w-full h-full object-cover sm:!w-[90px] sm:!h-[90px]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
                style={{ backgroundColor: `${TEAL}26` }}
              >
                <Store size={14} style={{ color: TEAL }} />
                <span className="text-xs font-semibold tracking-wider" style={{ color: TEAL }}>SHOP</span>
              </div>
              <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-white leading-tight -tracking-[0.5px] break-words">
                {shop.name}
              </h1>
              <div className="mt-2 flex items-center gap-1.5 text-zinc-400 text-sm">
                <MapPin size={18} className="shrink-0" />
                <span className="truncate">{addressLabel}</span>
              </div>
            </div>
          </div>

          {(shop.localDartTesting === true || shop.localPointChange === true) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {shop.localDartTesting === true && (
                <HeroTag icon={<TestTube size={14} style={{ color: TEAL }} />} label="Probewerfen vor Ort" />
              )}
              {shop.localPointChange === true && (
                <HeroTag icon={<Repeat size={14} style={{ color: TEAL }} />} label="Spitzenwechsel vor Ort" />
              )}
            </div>
          )}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 py-6 sm:py-10">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8 space-y-8">
            {/* Image Gallery */}
            {allImages.length > 0 && (
              <Section title="Galerie">
                <div className="rounded-2xl overflow-hidden bg-zinc-900 shadow-xl relative" style={{ height: 350 }}>
                  {currentImageUrl ? (
                    <img
                      src={currentImageUrl}
                      alt={`${shop.name} Bild ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openImageLightbox(currentImageIndex)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <Store size={64} />
                    </div>
                  )}
                  {currentImageUrl && (
                    <button
                      onClick={() => openImageLightbox(currentImageIndex)}
                      className="absolute bottom-4 left-4 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/70 text-white text-xs font-medium hover:bg-black/85 transition"
                    >
                      <Maximize2 size={16} />
                      Vollbild
                    </button>
                  )}
                  {allImages.length > 1 && (
                    <>
                      <button onClick={previousImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Vorheriges Bild">
                        <ChevronLeft size={26} />
                      </button>
                      <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Nächstes Bild">
                        <ChevronRight size={26} />
                      </button>
                      <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-semibold">
                        {currentImageIndex + 1} / {allImages.length}
                      </div>
                    </>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                    {allImages.map((id, idx) => {
                      const url = shopService.buildFileUrl(id);
                      const selected = idx === currentImageIndex;
                      return (
                        <button
                          key={`${id}-${idx}`}
                          onClick={() => openImageLightbox(idx)}
                          className="shrink-0 rounded-xl overflow-hidden bg-zinc-800"
                          style={{ width: 100, height: 80, border: `${selected ? 3 : 1}px solid ${selected ? TEAL : '#3f3f46'}` }}
                        >
                          {url ? (
                            <img src={url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500"><Store size={20} /></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Section>
            )}

            {/* Description */}
            {description.length > 0 && (
              <Section title="Über den Shop">
                <p className="whitespace-pre-wrap text-zinc-300 leading-[1.7] text-[15px]">{displayedDescription}</p>
                {isDescriptionLong && (
                  <button
                    onClick={() => setDescExpanded((v) => !v)}
                    className="mt-3 inline-flex items-center gap-1 text-[15px] font-semibold"
                    style={{ color: TEAL }}
                  >
                    {isDescriptionExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                    {isDescriptionExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                )}
              </Section>
            )}

            {/* Opening Hours */}
            {openingRows.length > 0 && (
              <Section title="Öffnungszeiten">
                <div className="space-y-3">
                  {openingRows.map((row, idx) => (
                    <ScheduleTimeRow key={`open-${row.dayKey}-${row.time}-${idx}`} row={row} label="Geöffnet" />
                  ))}
                </div>
              </Section>
            )}

            {/* Payment Methods */}
            {paymentMethods.length > 0 && (
              <Section title="Zahlungsmethoden">
                <div className="flex flex-wrap gap-2">
                  {paymentMethods.map((method, idx) => (
                    <div
                      key={`${method}-${idx}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-zinc-800 bg-zinc-900"
                    >
                      <CreditCard size={16} style={{ color: TEAL }} />
                      <span className="text-sm font-medium text-zinc-200">{method}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4 mt-8 lg:mt-0 space-y-5">
            {shop.shopLink && (
              <SidebarCard title="Online-Shop" icon={<ShoppingBag size={20} style={{ color: TEAL }} />}>
                <button
                  onClick={() => handleOpenShopLink(shop.shopLink)}
                  className="w-full px-4 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition"
                  style={{ backgroundColor: TEAL, color: '#000' }}
                >
                  <ExternalLink size={18} />
                  Zum Online-Shop
                </button>
                <p className="mt-3 text-xs text-zinc-500 break-all">{shop.shopLink}</p>
              </SidebarCard>
            )}

            {hasContact && (
              <SidebarCard title="Kontakt" icon={<Phone size={20} style={{ color: TEAL }} />}>
                <div className="space-y-3">
                  {shop.phone && (
                    <ContactItem
                      icon={<Phone size={20} style={{ color: TEAL }} />}
                      label={shop.phone}
                      onClick={() => handleCallPhone(shop.phone)}
                      onCopy={() => handleCopy(shop.phone || '', 'Telefonnummer kopiert')}
                    />
                  )}
                  {shop.email && (
                    <ContactItem
                      icon={<Mail size={20} style={{ color: TEAL }} />}
                      label={shop.email}
                      onClick={() => handleSendEmail(shop.email)}
                      onCopy={() => handleCopy(shop.email || '', 'E-Mail kopiert')}
                    />
                  )}
                </div>
              </SidebarCard>
            )}

            {(shop.id || shop._id) && (
              <SidebarCard title="Nachricht" icon={<MessageCircle size={20} style={{ color: TEAL }} />}>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                  Hast du Fragen zum Shop? Sende eine Nachricht über DartCloud.
                </p>
                <StartChatButton entryId={shop.id || shop._id} entryType="SHOP" />
              </SidebarCard>
            )}

            <SidebarCard title="Anfahrt" icon={<MapPin size={20} style={{ color: TEAL }} />}>
              <div className="flex items-start gap-3 mb-4">
                <MapPin size={20} style={{ color: TEAL }} className="shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-300 leading-[1.5]">{addressLabel}</p>
              </div>
              <MapPreview
                latitude={shop.address?.latitude || undefined}
                longitude={shop.address?.longitude || undefined}
                hasCoords={hasCoords}
                onClickRoute={handleOpenMaps}
              />
            </SidebarCard>
          </aside>
        </div>
      </main>

      {lightboxOpen && lightboxList[lightboxIndex] && (
        <Lightbox
          src={lightboxList[lightboxIndex]}
          counter={`${lightboxIndex + 1} / ${lightboxList.length}`}
          showNav={lightboxList.length > 1}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setLightboxIndex((i) => (i > 0 ? i - 1 : lightboxList.length - 1))}
          onNext={() => setLightboxIndex((i) => (i < lightboxList.length - 1 ? i + 1 : 0))}
        />
      )}

      {externalLink && (
        <ExternalLinkModal
          url={externalLink}
          onCancel={() => setExternalLink(null)}
          onConfirm={() => {
            const url = externalLink;
            setExternalLink(null);
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          }}
        />
      )}

      {copyToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-full shadow-lg text-sm font-medium bg-zinc-900 border border-zinc-700 text-white">
          {copyToast}
        </div>
      )}
    </>
  );
};

// ===================================================================
// Subcomponents
// ===================================================================

const ActionButton: React.FC<{ icon: React.ReactNode; onClick: () => void; active?: boolean }> = ({ icon, onClick, active }) => (
  <button
    onClick={onClick}
    className="p-3 rounded-xl border transition"
    style={{
      borderColor: active ? TEAL : '#3f3f46',
      backgroundColor: active ? `${TEAL}26` : '#27272a',
      color: active ? TEAL : '#d4d4d8',
    }}
  >
    {icon}
  </button>
);

const HeroTag: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-700 bg-zinc-800">
    {icon}
    <span className="text-xs font-medium text-zinc-300">{label}</span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h2 className="text-xl sm:text-[22px] font-bold text-white -tracking-[0.5px] mb-4">{title}</h2>
    {children}
  </section>
);

const ScheduleTimeRow: React.FC<{ row: ScheduleRow; label: string }> = ({ row, label }) => (
  <div
    className="flex items-center gap-3 p-4 rounded-2xl border"
    style={{
      backgroundColor: row.isToday ? `${TEAL}1A` : '#1f1f23',
      borderColor: row.isToday ? `${TEAL}4D` : '#27272a',
    }}
  >
    <div className="flex items-center gap-2 w-24 shrink-0">
      {row.isToday && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TEAL }} />}
      <span
        className="text-[15px]"
        style={{
          fontWeight: row.isToday ? 700 : 500,
          color: row.isToday ? TEAL : '#fff',
        }}
      >
        {row.dayLabel}
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[15px] font-semibold text-white">{row.time}</div>
      <div className="text-[13px] text-zinc-400 inline-flex items-center gap-1.5">
        <Clock size={12} />
        {label}
      </div>
    </div>
  </div>
);

const SidebarCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <section className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${TEAL}1A` }}>
        {icon}
      </div>
      <h3 className="text-[17px] font-bold text-white">{title}</h3>
    </div>
    {children}
  </section>
);

const ContactItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  onCopy: () => void;
}> = ({ icon, label, onClick, onCopy }) => (
  <div className="flex items-center gap-3">
    <button onClick={onClick} className="flex-1 min-w-0 flex items-center gap-3 py-1.5 hover:opacity-80 transition">
      {icon}
      <span className="text-sm text-zinc-300 truncate">{label}</span>
      <ChevronRightIcon size={14} className="text-zinc-500 ml-auto shrink-0" />
    </button>
    <button
      onClick={onCopy}
      className="p-2 rounded-lg border border-zinc-800 hover:bg-zinc-800 transition text-zinc-400 hover:text-white"
      aria-label="Kopieren"
    >
      <Copy size={14} />
    </button>
  </div>
);

const MapPreview: React.FC<{
  latitude?: number;
  longitude?: number;
  hasCoords: boolean;
  onClickRoute: () => void;
}> = ({ latitude, longitude, hasCoords, onClickRoute }) => {
  const iframeSrc = hasCoords && typeof latitude === 'number' && typeof longitude === 'number'
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.005},${latitude - 0.0025},${longitude + 0.005},${latitude + 0.0025}&layer=mapnik&marker=${latitude},${longitude}`
    : null;

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-zinc-800" style={{ height: 180 }}>
      {iframeSrc ? (
        <iframe
          src={iframeSrc}
          title="Karte"
          className="w-full h-full border-0 pointer-events-none"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
          <MapPin size={48} />
        </div>
      )}
      <button
        onClick={onClickRoute}
        className="absolute bottom-0 left-0 right-0 py-3 flex items-center justify-center gap-2 text-white text-sm font-semibold"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.8))' }}
      >
        <Navigation size={18} />
        Route in Maps öffnen
      </button>
    </div>
  );
};

const Lightbox: React.FC<{
  src: string;
  counter: string;
  showNav: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}> = ({ src, counter, showNav, onClose, onPrev, onNext }) => (
  <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
    <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white" aria-label="Schließen">
      <X size={24} />
    </button>
    <img src={src} alt="Vollbild" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
    {showNav && (
      <>
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center" aria-label="Vorheriges Bild">
          <ChevronLeft size={28} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center" aria-label="Nächstes Bild">
          <ChevronRight size={28} />
        </button>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-zinc-800/80 text-white text-sm">
          {counter}
        </div>
      </>
    )}
  </div>
);

const ExternalLinkModal: React.FC<{
  url: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ url, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[65] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-3 mb-3">
        <AlertCircle size={28} style={{ color: WARN }} />
        <h3 className="text-lg font-bold text-white">Externer Link</h3>
      </div>
      <p className="text-sm text-zinc-300">Du wirst zu einer externen Webseite weitergeleitet.</p>
      <div className="mt-3 p-3 rounded-lg bg-zinc-800 text-xs text-zinc-400 break-all">{url}</div>
      <p className="mt-3 text-xs italic text-zinc-500">
        Wir übernehmen keine Verantwortung für den Inhalt externer Webseiten.
      </p>
      <div className="mt-6 flex gap-3">
        <button onClick={onCancel} className="flex-1 px-4 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium">
          Abbrechen
        </button>
        <button onClick={onConfirm} className="flex-1 px-4 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2" style={{ backgroundColor: TEAL, color: '#000' }}>
          <ExternalLink size={18} />
          Link öffnen
        </button>
      </div>
    </div>
  </div>
);

export default ShopDetailPage;
